import React from 'react';
import {
  Handle,
  Position,
  type Node,
  type NodeProps,
  type NodeTypes,
} from '@xyflow/react';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Loader2,
  RotateCcw,
  Workflow,
} from 'lucide-react';
import { Text } from '@cotera/client/app/components/ui';
import { Badge } from '@cotera/client/v0/components/ui/badge';
import { cn } from '@cotera/client/v0/lib/utils';
import {
  WORKFLOW_FLOW_NODE_TYPE,
  WORKFLOW_FLOW_TRIGGER_TYPE,
  WorkflowFlowPreviewTrigger,
  WorkflowNode,
} from './components';
import { NODE_ACCENTS } from './components/node-accent';
import {
  NESTED_GROUP_HEADER_HEIGHT,
  NESTED_RUNS_SOURCE_HANDLE_ID,
  NESTED_WORKFLOW_GROUP_NODE_TYPE,
  type NestedWorkflowCardNodeData,
  type NestedWorkflowGroupNodeData,
} from './nested-workflow-tree-to-flow';
import {
  useOpenNestedWorkflow,
  useSetNestedGroupCollapsed,
} from './nested-workflow-tree-context';
import {
  NestedTreeControlButton,
  NestedWorkflowCardAffordance,
} from './nested-workflow-tree-affordances';

const PLACEHOLDER_COPY: Record<
  NonNullable<NestedWorkflowGroupNodeData['placeholder']>,
  { label: string; detail: string; tone: 'muted' | 'warning' }
> = {
  loading: {
    label: 'Loading this workflow…',
    detail: 'Fetching its graph.',
    tone: 'muted',
  },
  missing: {
    label: 'Workflow unavailable',
    detail: 'It may have been deleted, or you may not have access to it.',
    tone: 'warning',
  },
  cycle: {
    label: 'Already shown further left',
    detail:
      'This workflow runs one of its own callers, so the chain stops here.',
    tone: 'warning',
  },
  'too-deep': {
    label: 'Nested too deep to show',
    detail: 'Open this workflow to keep going down.',
    tone: 'muted',
  },
};

function groupLabel(data: NestedWorkflowGroupNodeData): string {
  const { name, workflowId } = data;
  if (name !== null && name.trim() !== '') {
    return name.trim();
  }
  return `Workflow ${workflowId.slice(0, 8)}`;
}

/**
 * The box around one workflow's graph.
 *
 * A React Flow parent node: the workflow's own cards are its children, so they
 * keep the layout the editor canvas gave them and this only has to draw the frame
 * and the title bar. Its handles are on the sides, because nesting runs left to
 * right while every workflow inside still runs top to bottom.
 */
const NestedWorkflowGroupNode: React.FC<
  NodeProps<Node<NestedWorkflowGroupNodeData>>
> = ({ id, data }) => {
  const openWorkflow = useOpenNestedWorkflow();
  const setCollapsed = useSetNestedGroupCollapsed();
  const accent = NODE_ACCENTS['workflow'];
  const label = groupLabel(data);
  const placeholder =
    data.placeholder === null ? null : PLACEHOLDER_COPY[data.placeholder];
  const canOpen = openWorkflow !== null && !data.isRoot;
  // `data.canCollapse` is "has steps worth hiding"; this is that *and* a host
  // wired to receive the toggle.
  const showCollapseControl = setCollapsed !== null && data.canCollapse;

  return (
    <div
      className={cn(
        'flex h-full w-full flex-col overflow-hidden rounded-xl border-2',
        data.isRoot
          ? 'border-primary/40 bg-primary/[0.03]'
          : 'border-border bg-muted/25',
        placeholder !== null && 'border-dashed'
      )}
    >
      <Handle
        type="target"
        position={Position.Left}
        isConnectable={false}
        className="opacity-0"
        // Centred on the title bar, not on the box: a tall workflow would
        // otherwise take the arrow into the middle of its graph, which reads as
        // pointing at whatever step happens to sit there.
        style={{ top: NESTED_GROUP_HEADER_HEIGHT / 2 }}
      />
      <Handle
        type="source"
        position={Position.Right}
        isConnectable={false}
        className="opacity-0"
      />
      <div
        className={cn(
          'flex shrink-0 items-center gap-2 bg-background/70 px-3 py-2',
          // No rule under the header when there is nothing below it to divide.
          data.isCollapsed ? 'h-full rounded-[10px]' : 'border-b'
        )}
      >
        {showCollapseControl ? (
          <NestedTreeControlButton
            title={data.isCollapsed ? 'Show steps' : 'Hide steps'}
            aria-label={
              data.isCollapsed
                ? `Show steps in ${label}`
                : `Hide steps in ${label}`
            }
            aria-expanded={!data.isCollapsed}
            shape="header"
            onClick={() => setCollapsed(id, !data.isCollapsed)}
          >
            {data.isCollapsed ? (
              <ChevronRight className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
          </NestedTreeControlButton>
        ) : null}
        <span
          className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-md [&_svg]:h-3.5 [&_svg]:w-3.5',
            accent.chip
          )}
        >
          <Workflow />
        </span>
        <div className="min-w-0 flex-1">
          <Text.Caption className="text-foreground truncate font-medium">
            {label}
          </Text.Caption>
        </div>
        {data.isCollapsed ? (
          <Text.Caption className="text-muted-foreground shrink-0">
            {data.stepCount === 1 ? '1 step' : `${data.stepCount} steps`}
          </Text.Caption>
        ) : null}
        <Badge
          variant={data.isRoot ? 'primary' : 'gray'}
          size="sm"
          className="shrink-0"
        >
          {data.isRoot ? 'This workflow' : `Level ${data.depth}`}
        </Badge>
        {canOpen ? (
          <NestedTreeControlButton
            title="Open this workflow"
            aria-label={`Open ${label}`}
            shape="header"
            onClick={() => {
              openWorkflow({
                workflowId: data.workflowId,
                name: data.name,
              });
            }}
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </NestedTreeControlButton>
        ) : null}
      </div>

      {placeholder !== null ? (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-1.5 px-4 text-center">
          <span
            className={cn(
              'flex h-5 w-5 items-center justify-center [&_svg]:h-4 [&_svg]:w-4',
              placeholder.tone === 'warning'
                ? 'text-amber-600'
                : 'text-muted-foreground'
            )}
          >
            {data.placeholder === 'loading' ? (
              <Loader2 className="animate-spin" />
            ) : data.placeholder === 'cycle' ? (
              <RotateCcw />
            ) : (
              <AlertTriangle />
            )}
          </span>
          <Text.Caption className="text-foreground font-medium">
            {placeholder.label}
          </Text.Caption>
          <Text.Caption className="text-muted-foreground">
            {placeholder.detail}
          </Text.Caption>
        </div>
      ) : null}
    </div>
  );
};

/**
 * The same cards the editor canvas draws, minus the per-node view models.
 *
 * One workflow can appear more than once in a tree, and the canvas node wrapper
 * registers a model keyed on the entity it shows — two copies of one agent would
 * register the same id twice. This view is read-only, so it renders the card
 * directly and skips that entirely (the run-workflow drawer's preview does the
 * same).
 */
const NestedTreeNode: React.FC<NodeProps<Node<NestedWorkflowCardNodeData>>> = ({
  data,
}) => {
  return (
    // Same corner placement the editor canvas uses for its per-node affordances,
    // so the control reads as belonging to the card rather than floating near it.
    <div className="group relative w-fit">
      <WorkflowNode node={data.node} />
      {data.runsNestedWorkflow ? (
        <Handle
          type="source"
          id={NESTED_RUNS_SOURCE_HANDLE_ID}
          position={Position.Right}
          isConnectable={false}
          className="opacity-0"
        />
      ) : null}
      <div className="absolute -right-2 -top-2 flex items-center gap-1">
        <NestedWorkflowCardAffordance data={data} />
      </div>
    </div>
  );
};

/** The node types this canvas renders, keyed as the layout stamps them. */
export const NESTED_TREE_NODE_TYPES: NodeTypes = {
  [NESTED_WORKFLOW_GROUP_NODE_TYPE]: NestedWorkflowGroupNode,
  [WORKFLOW_FLOW_NODE_TYPE]: NestedTreeNode,
  // The shared read-only trigger card: nothing about it differs here.
  [WORKFLOW_FLOW_TRIGGER_TYPE]: WorkflowFlowPreviewTrigger,
};
